import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import qs from 'qs';

import {
	categories,
	typeTitles,
	linkWithQueryParams,
	queryParamsFromFilters,
} from 'inside-out/managers/resourceLibraryManager';

import { navigateTo } from 'inside-out/components/shared/link';
import { PageNav } from 'inside-out/components/shared/pageNav';
import { AudienceDropDown } from 'inside-out/components/resource-library/audienceDropDown';

import { LandingBlock } from 'inside-out/components/resource-library/landingBlock';
import { ResultsBlock } from 'inside-out/components/resource-library/resultsBlock';
import { SearchFilter } from 'inside-out/components/resource-library/searchFilter';


import {
	tabPageWrapper,
	innerWrapperStyle,
	headerWrapperStyle,
	navContainerStyle,
	navStyle,
	titleStyle,
	formStyle,
	dropdownStyle,
	spacerStyle,
} from 'inside-out/page-styles/resource-library';


const defaultFilters = { query: '', audience: '', type: '', page: 1 };

export default class ResourceLibraryPage extends React.PureComponent {
	static propTypes = {
		data: PropTypes.object
	};

	state = {
		loading: true,
		error: null,
		searchResults: {
			totalResources: 0,
			totalPages: 0,
			nodes: []
		},
		searchField: '',
		filters: defaultFilters,
	};

	blockSearch = false;

	componentDidMount = () => {
		this.unlisten = this.props.history.listen(this.didChangeRoute); // eslint-disable-line
		this.updateStateFromParams();
	}

	componentWillUnmount() {
		if (typeof this.unlisten === 'function') {
			this.unlisten();
		}
	}

	didChangeRoute = () => {
		if (!this.blockSearch) {
			this.updateStateFromParams();
		}
	}

	updateStateFromParams = () => {
		const params = qs.parse(location.search, { ignoreQueryPrefix: true });
		this.setState({
			searchField: params.query || '',
			filters: {
				...defaultFilters,
				...params
			}
		}, () => {
			this.search(true);
		});
	}

	updateFilters = (filter, search = false, preservePage = false) => {
		if (search) {
			this.blockSearch = true;
		}
		this.setState(prevState => {
			let filters = {
				...prevState.filters,
				...filter,
				...(preservePage ? null : { page: 1 })
			};
			return {
				...prevState,
				filters
			};
		}, () => {
			if (search) this.search();
		});
	}

	updateQuery = () => {
		this.setState(prevState => {
			let filters = {
				...prevState.filters,
				query: prevState.searchField,
				page: 1
			};
			return {
				...prevState,
				filters
			};
		}, this.search);
	}

	search = passive => {
		this.setState(prevState => ({ loading: true, searchField: prevState.filters.query }), async () => {
			try {
				this.blockSearch = true;
				const base = location.hostname === 'localhost'
					? 'http://localhost:9000'
					: '/.netlify/functions';

				const queryParams = queryParamsFromFilters(this.state.filters);

				if (!passive) {
					// update the query string
					navigateTo(linkWithQueryParams(queryParams));
				}

				let searchResults = {
					totalResources: 0,
					totalPages: 0,
					nodes: [],
				};
				// request data if filters aren't empty
				if (queryParams !== '') {
					const results = await fetch(`${ base }/search-resource-library?${ queryParams }`);
					const parsed = await results.json();
					searchResults = parsed;
				}

				this.setState({ loading: false, searchResults });
				this.blockSearch = false;
			} catch (error) {
				console.warn(error);
				this.setState({ loading: false, error });
				this.blockSearch = false;
			}
		});
	}

	areFiltersEmpty = (filters) => !filters.query && !filters.audience && !filters.type;

	render() {
		const { page } = this.props.data;
		const { loading, searchField, filters, searchResults, } = this.state;
		const shouldShowLandingPage = this.areFiltersEmpty(filters);

		return (
			<React.Fragment>
				<Helmet title="Resource Library">
					<meta property="og:type" content="website"/>
					<meta property="og:title" content="InsideOut - Resource Library"/>
				</Helmet>

				<div className={headerWrapperStyle}>
					<h1 className={titleStyle}>Learn more about eating disorders</h1>

					<div className={formStyle}>
						<AudienceDropDown
							value={filters.audience}
							onChange={audience => this.updateFilters({ audience, type: categories[audience] ? categories[audience][0] : '' }, true)}
							className={dropdownStyle}/>
						<SearchFilter
							placeholder='e.g. First symptoms of eating disorder'
							value={searchField}
							onChange={query => this.setState({ searchField: query })}
							onSubmit={this.updateQuery}/>
					</div>
				</div>

				{filters.audience && (
					<div className={navContainerStyle}>
						<PageNav
							className={navStyle}
							links={categories[filters.audience].map(type => ({
								to: linkWithQueryParams(queryParamsFromFilters({ audience: filters.audience, type })),
								text: typeTitles[type],
								managedKey: type,
							}))}

							managed={{
								activeLink: filters.type,
								onClick: (type, e) => {
									e.preventDefault();
									if (type === this.state.filters.type) return;
									this.updateFilters({ type }, true);
								}
							}}
						/>
					</div>
				)}
				{!filters.audience && (
					<div className={spacerStyle}/>
				)}

				<div className={tabPageWrapper}>
					<div className={innerWrapperStyle}>
						{loading && (
							<p>Loading...</p>
						)}
						{!loading && shouldShowLandingPage && (
							<LandingBlock data={this.props.data} page={page} onSectionSelect={section => this.updateFilters(section, true)}/>
						)}
						{!loading && !shouldShowLandingPage && (
							<ResultsBlock
								data={searchResults}
								page={page}
								filters={filters}
								onPageChange={page => this.updateFilters({ page }, true, true)}/>
						)}
					</div>
				</div>
			</React.Fragment>
		);
	}
}

export const query = graphql`
	fragment resourceCategories on ResourceLibraryJson {
		categories {
			me {
				enabled
				understanding_eating_disorders
				seeking_treatment
				self_help
				personal_stories
			}
			carers {
				enabled
				understanding_eating_disorders
				supporting_someone_with_an_eating_disorder
				personal_stories
				seeking_treatment
			}
			researchers {
				enabled
				publications
			}
			service_development {
				enabled
				forums
				guidelines
				nsw_updates
			}
			professionals {
				enabled
				screening_and_assessment
				understanding_eating_disorders
				treatment
				guidelines
				client_handouts
				elearning
			}
		}
	}
	fragment resourceItem on ResourceLibraryJson {
		id
		title
		extract
		date
		fields {
			thumbnail_image {
				childImageSharp {
					resize(width: 600, height: 320, cropFocus: CENTER) {
						src
					}
				}
			}
		}
	}
	fragment resource on ResourceLibraryJson {
		...resourceItem
		tile_button_text
		...resourceCategories
		attachments {
			vimeo_url
		}
	}

	fragment resources on ResourceLibraryJsonConnection {
		edges {
			node {
				...resource
			}
		}
	}

	query ResourceLibraryQuery {
		page: allContentJson(filter: { id: { regex: "/content\\/resource-library-page\\.json/" } }) {
			edges {
				node {
					purple_heading
					purple_body
					button_text
					button_to
				}
			}
		}

		recentELearnings: allResourceLibraryJson(
			filter: { categories: { professionals: { elearning: { eq: true } } } }
			sort: { order: DESC, fields: [date] }
			limit: 3
		) {
			...resources
		}

		recentForMe: allResourceLibraryJson(
			filter: { categories: { me: { enabled: { eq: true } } } }
			sort: { order: DESC, fields: [date] }
			limit: 4
		) {
			...resources
		}

		recentForCarers: allResourceLibraryJson(
			filter: { categories: { carers: { enabled: { eq: true } } } }
			sort: { order: DESC, fields: [date] }
			limit: 4
		) {
			...resources
		}

		recentForResearchers: allResourceLibraryJson(
			filter: { categories: { researchers: { enabled: { eq: true } } } }
			sort: { order: DESC, fields: [date] }
			limit: 4
		) {
			...resources
		}

		recentForServiceDevelopment: allResourceLibraryJson(
			filter: { categories: { service_development: { enabled: { eq: true } } } }
			sort: { order: DESC, fields: [date] }
			limit: 4
		) {
			...resources
		}
	}
`;



// WEBPACK FOOTER //
// ./src/pages/resource-library/index.js